import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { useMailStore } from '../../stores/mailStore';

interface Folder {
  id: string;
  name: string;
  color: string;
  parentId?: string;
  isSystem: boolean;
}

const FoldersSettings: React.FC = () => {
  const { folders: storeFolders, fetchFolders, createFolder, deleteFolder, renameFolder } = useMailStore();
  
  // Map store folders to local format
  const systemFolderIds = ['inbox', 'drafts', 'sent', 'spam', 'trash', 'starred', 'important'];
  const folders: Folder[] = storeFolders.map(f => ({
    id: f.id,
    name: f.name,
    color: f.color || '#5f6368',
    parentId: f.parentId,
    isSystem: systemFolderIds.includes(f.id) || f.type === 'system',
  }));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('#1a73e8');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const colorOptions = [
    '#ea4335', '#fbbc05', '#34a853', '#4285f4', '#9c27b0',
    '#ff5722', '#795548', '#607d8b', '#00bcd4', '#e91e63',
  ];

  const handleOpenDialog = (folder?: Folder) => {
    if (folder) {
      setEditingFolder(folder);
      setFolderName(folder.name);
      setFolderColor(folder.color);
    } else {
      setEditingFolder(null);
      setFolderName('');
      setFolderColor('#1a73e8');
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFolder(null);
  };

  const handleSaveFolder = async () => {
    setIsLoading(true);
    try {
      if (editingFolder) {
        await renameFolder(editingFolder.id, folderName, folderColor);
        setSnackbar({ open: true, message: 'Folder updated successfully', severity: 'success' });
      } else {
        await createFolder(folderName, undefined, folderColor);
        setSnackbar({ open: true, message: 'Folder created successfully', severity: 'success' });
      }
      handleCloseDialog();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to save folder', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFolder = async (id: string) => {
    const folder = folders.find(f => f.id === id);
    if (folder) {
      setFolderToDelete(folder);
      setDeleteConfirmOpen(true);
    }
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;
    setIsLoading(true);
    try {
      await deleteFolder(folderToDelete.id);
      setSnackbar({ open: true, message: 'Folder deleted successfully', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to delete folder', severity: 'error' });
    } finally {
      setIsLoading(false);
      setDeleteConfirmOpen(false);
      setFolderToDelete(null);
    }
  };

  const systemFolders = folders.filter(f => f.isSystem);
  const customFolders = folders.filter(f => !f.isSystem);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 1 }}>
        Folders
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your email folders and organize your inbox
      </Typography>

      {/* Add folder button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Folder
        </Button>
      </Box>

      {/* System folders */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
          SYSTEM FOLDERS
        </Typography>
        <Paper variant="outlined" sx={{ borderRadius: 1 }}>
          <List disablePadding>
            {systemFolders.map((folder, index) => (
              <ListItem
                key={folder.id}
                sx={{
                  borderBottom: index < systemFolders.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <FolderIcon sx={{ color: folder.color, mr: 2 }} />
                <ListItemText
                  primary={folder.name}
                  secondary="System folder - cannot be deleted"
                />
                <Chip label="System" size="small" sx={{ bgcolor: 'action.selected' }} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>

      {/* Custom folders */}
      <Box>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
          CUSTOM FOLDERS
        </Typography>
        {customFolders.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              No custom folders. Create a folder to organize your emails.
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 1 }}>
            <List disablePadding>
              {customFolders.map((folder, index) => (
                <ListItem
                  key={folder.id}
                  secondaryAction={
                    <>
                      <IconButton onClick={() => handleOpenDialog(folder)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteFolder(folder.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  }
                  sx={{
                    borderBottom: index < customFolders.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  <FolderOpenIcon sx={{ color: folder.color, mr: 2 }} />
                  <ListItemText primary={folder.name} />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>

      {/* Folder dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingFolder ? 'Edit Folder' : 'Create Folder'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Folder name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
              Folder color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {colorOptions.map((color) => (
                <Box
                  key={color}
                  onClick={() => setFolderColor(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: color,
                    cursor: 'pointer',
                    border: folderColor === color ? '3px solid' : '2px solid transparent',
                    borderColor: folderColor === color ? 'text.primary' : 'transparent',
                    '&:hover': { opacity: 0.8 },
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveFolder}
            disabled={!folderName.trim() || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {editingFolder ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{folderToDelete?.name}"? Emails in this folder will be moved to Inbox.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteFolder}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FoldersSettings;
